import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  FlaskConical,
  Trophy,
  Users,
  MousePointerClick,
  BarChart3,
  Clock,
  Play,
  Pause,
  StopCircle,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import abTestService, { ABTest } from '../../services/abTestService';
import Button from '../ui/Button';
import Badge from '../ui/Badge';

interface ABTestDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  testId: string;
  onUpdate?: () => void;
}

const ABTestDetailModal: React.FC<ABTestDetailModalProps> = ({
  isOpen,
  onClose,
  testId,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [test, setTest] = useState<ABTest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadTest = async () => {
      if (!testId) return;
      setIsLoading(true);
      try {
        const response = await abTestService.getTest(testId);
        if (response.success && response.data) {
          setTest(response.data);
        }
      } catch (error) {
        console.error('Error loading test:', error);
        toast.error('Error loading test');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadTest();
    }
  }, [isOpen, testId]);

  const handleStart = async () => {
    setIsUpdating(true);
    try {
      const response = await abTestService.updateTest(testId, { status: 'running' });
      if (response.success && response.data) {
        setTest(response.data);
        toast.success(t('abTesting.testStarted', 'Test started!'));
        onUpdate?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error starting test');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePause = async () => {
    setIsUpdating(true);
    try {
      const response = await abTestService.updateTest(testId, { status: 'paused' });
      if (response.success && response.data) {
        setTest(response.data);
        toast.success(t('abTesting.testPaused', 'Test paused'));
        onUpdate?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error pausing test');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEnd = async () => {
    if (!window.confirm(t('abTesting.confirmEnd', 'Tem certeza que deseja encerrar este teste? Esta acao nao pode ser desfeita.'))) {
      return;
    }

    setIsUpdating(true);
    try {
      const response = await abTestService.endTest(testId);
      if (response.success && response.data) {
        setTest(response.data);
        toast.success(t('abTesting.testEnded', 'Test ended!'));
        onUpdate?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error ending test');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { variant: 'gray' | 'success' | 'warning' | 'primary'; label: string }> = {
      draft: { variant: 'gray', label: t('abTesting.status.draft', 'Rascunho') },
      running: { variant: 'success', label: t('abTesting.status.running', 'Em andamento') },
      paused: { variant: 'warning', label: t('abTesting.status.paused', 'Pausado') },
      completed: { variant: 'primary', label: t('abTesting.status.completed', 'Concluido') }
    };
    return configs[status] || configs.draft;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = test?.stats;
  const significance = test?.significance;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 95) return 'text-green-600 bg-green-50 dark:bg-green-900/20';
    if (confidence >= 90) return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
    return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FlaskConical className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {test?.name || t('abTesting.testDetails', 'Detalhes do Teste')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {test?.landingPage?.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {test && <Badge variant={getStatusConfig(test.status).variant}>{getStatusConfig(test.status).label}</Badge>}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : test ? (
            <div className="space-y-6">
              {/* Significance Banner */}
              {significance && (
                <div className={`p-4 rounded-xl border ${
                  significance.isSignificant
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-start gap-3">
                    {significance.isSignificant ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-gray-500 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${significance.isSignificant ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {significance.recommendation}
                      </p>
                      {significance.confidenceLevel && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('abTesting.statisticalConfidence', 'Confianca estatistica')}: {significance.confidenceLevel}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl text-center">
                  <Users className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.totalViews.toLocaleString('pt-BR') || 0}
                  </p>
                  <p className="text-sm text-gray-500">{t('abTesting.totalViews', 'Total de Visitas')}</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl text-center">
                  <MousePointerClick className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.totalConversions.toLocaleString('pt-BR') || 0}
                  </p>
                  <p className="text-sm text-gray-500">{t('abTesting.totalConversions', 'Total de Conversoes')}</p>
                </div>
                <div className={`p-4 rounded-xl text-center ${getConfidenceColor(significance?.confidence || 0)}`}>
                  <BarChart3 className="w-6 h-6 mx-auto mb-2 opacity-60" />
                  <p className="text-2xl font-bold">
                    {significance?.confidence?.toFixed(1) || 0}%
                  </p>
                  <p className="text-sm opacity-80">{t('abTesting.confidence', 'Confianca')}</p>
                </div>
              </div>

              {/* Page Previews */}
              {test.variants && test.variants.length >= 2 && (
                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-5 h-5 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">
                      {t('abTesting.pagePreview', 'Preview das Paginas')}
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {test.variants.map((variant) => (
                      <a
                        key={variant.id}
                        href={variant.landingPage?.published_url || `/landing-pages/${variant.landing_page_id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          variant.is_control
                            ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:shadow-sm'
                            : 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-300 hover:shadow-sm'
                        }`}
                      >
                        <ExternalLink className={`w-4 h-4 flex-shrink-0 ${variant.is_control ? 'text-gray-500' : 'text-indigo-500'}`} />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium truncate ${variant.is_control ? 'text-gray-700 dark:text-gray-300' : 'text-indigo-700 dark:text-indigo-300'}`}>
                            {variant.landingPage?.name || variant.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {variant.is_control ? t('abTesting.control', 'Controle') : t('abTesting.variant', 'Variante')}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Variants Comparison */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('abTesting.variantsComparison', 'Comparacao de Variantes')}
                </h3>
                <div className="space-y-4">
                  {stats?.variants.map((variant, index) => {
                    const isWinner = test.winner_variant_id === variant.id;
                    const otherVariant = stats.variants.find(v => v.id !== variant.id);
                    const improvement = otherVariant && variant.conversionRate > 0 && otherVariant.conversionRate > 0
                      ? ((variant.conversionRate - otherVariant.conversionRate) / otherVariant.conversionRate * 100)
                      : null;

                    return (
                      <div
                        key={variant.id}
                        className={`p-5 rounded-xl border ${
                          isWinner
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : variant.isControl
                            ? 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700'
                            : 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-semibold uppercase ${
                              variant.isControl ? 'text-gray-600' : 'text-indigo-600'
                            }`}>
                              {variant.name}
                            </span>
                            {isWinner && (
                              <span className="flex items-center gap-1 text-sm font-medium text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5 rounded-full">
                                <Trophy className="w-4 h-4" />
                                {t('abTesting.winner', 'Vencedor')}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {variant.trafficPercentage}% {t('abTesting.traffic', 'do trafego')}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{t('abTesting.views', 'Visitas')}</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              {variant.views.toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{t('abTesting.conversions', 'Conversoes')}</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              {variant.conversions.toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{t('abTesting.conversionRate', 'Taxa')}</p>
                            <div className="flex items-center gap-2">
                              <p className={`text-xl font-bold ${
                                variant.conversionRate >= 5 ? 'text-green-600' :
                                variant.conversionRate >= 2 ? 'text-yellow-600' : 'text-gray-900 dark:text-white'
                              }`}>
                                {variant.conversionRate.toFixed(2)}%
                              </p>
                              {improvement !== null && !variant.isControl && (
                                <span className={`text-sm flex items-center gap-0.5 ${
                                  improvement > 0 ? 'text-green-600' : improvement < 0 ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                  {improvement > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                  {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mt-4">
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                isWinner ? 'bg-green-500' : variant.isControl ? 'bg-gray-400' : 'bg-indigo-500'
                              }`}
                              style={{ width: `${Math.min(variant.conversionRate * 10, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Test Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{t('abTesting.startedAt', 'Iniciado em')}:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{formatDate(test.started_at)}</span>
                </div>
                {test.ended_at && (
                  <div>
                    <span className="text-gray-500">{t('abTesting.endedAt', 'Encerrado em')}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{formatDate(test.ended_at)}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500">{t('abTesting.testNotFound', 'Teste nao encontrado')}</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {test && test.status !== 'completed' && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            {test.status === 'draft' && (
              <Button
                onClick={handleStart}
                isLoading={isUpdating}
                leftIcon={<Play className="w-4 h-4" />}
              >
                {t('abTesting.startTest', 'Iniciar Teste')}
              </Button>
            )}
            {test.status === 'running' && (
              <>
                <Button
                  variant="secondary"
                  onClick={handlePause}
                  isLoading={isUpdating}
                  leftIcon={<Pause className="w-4 h-4" />}
                >
                  {t('abTesting.pauseTest', 'Pausar')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleEnd}
                  isLoading={isUpdating}
                  leftIcon={<StopCircle className="w-4 h-4" />}
                >
                  {t('abTesting.endTest', 'Encerrar')}
                </Button>
              </>
            )}
            {test.status === 'paused' && (
              <>
                <Button
                  onClick={handleStart}
                  isLoading={isUpdating}
                  leftIcon={<Play className="w-4 h-4" />}
                >
                  {t('abTesting.resumeTest', 'Retomar')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleEnd}
                  isLoading={isUpdating}
                  leftIcon={<StopCircle className="w-4 h-4" />}
                >
                  {t('abTesting.endTest', 'Encerrar')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ABTestDetailModal;
